import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const UserManagementCenter = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [filterRole]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (filterRole !== 'all') {
        query = query?.eq('role', filterRole);
      }

      const { data, error } = await query;

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e?.target?.value);
  };

  const filteredUsers = users?.filter((user) => {
    const matchesSearch = 
      user?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesSearch;
  });

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev?.includes(userId) ? prev?.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers?.length === filteredUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers?.map((user) => user?.id));
    }
  };

  const handleSuspendUser = async (userId) => {
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ account_status: 'suspended' })?.eq('id', userId);

      if (error) throw error;
      await fetchUsers();
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error suspending user:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateUser = async (userId) => {
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ account_status: 'active' })?.eq('id', userId);

      if (error) throw error;
      await fetchUsers();
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error activating user:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers?.length === 0) return;

    setActionLoading(true);
    try {
      const updates = { account_status: action === 'suspend' ? 'suspended' : 'active' };
      
      const { error } = await supabase?.from('user_profiles')?.update(updates)?.in('id', selectedUsers);

      if (error) throw error;
      await fetchUsers();
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      owner: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      renter: 'bg-green-500/10 text-green-600 border-green-500/20'
    };
    return colors?.[role] || colors?.renter;
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-green-500/10 text-green-600',
      suspended: 'bg-red-500/10 text-red-600',
      pending: 'bg-yellow-500/10 text-yellow-600'
    };
    return colors?.[status] || colors?.active;
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-foreground mb-1 flex items-center">
          <Icon name="Users" size={24} className="text-primary mr-2" />
          User Management Center
        </h2>
        <p className="font-body text-sm text-muted-foreground">Comprehensive account oversight and administration</p>
      </div>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-10 w-full"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
            className="px-4 py-2 bg-muted border border-border rounded-lg font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admins</option>
            <option value="owner">Owners</option>
            <option value="renter">Renters</option>
          </select>

          <Button onClick={fetchUsers} variant="outline" size="sm">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      {/* Bulk Actions */}
      {selectedUsers?.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="font-body text-sm text-foreground">
              <span className="font-semibold">{selectedUsers?.length}</span> user{selectedUsers?.length > 1 ? 's' : ''} selected
            </p>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => handleBulkAction('activate')}
                disabled={actionLoading}
                size="sm"
                variant="outline"
              >
                <Icon name="CheckCircle" size={16} className="mr-2" />
                Activate
              </Button>
              <Button
                onClick={() => handleBulkAction('suspend')}
                disabled={actionLoading}
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-500/10"
              >
                <Icon name="XCircle" size={16} className="mr-2" />
                Suspend
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Users Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)]?.map((_, index) => (
              <div key={index} className="bg-muted animate-pulse rounded-lg h-20" />
            ))}
          </div>
        ) : filteredUsers?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="font-body text-muted-foreground">No users found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Table Header */}
            <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-4 py-3 bg-muted/50 rounded-lg font-body text-sm font-semibold text-muted-foreground">
              <div className="col-span-1 flex items-center">
                <input
                  type="checkbox"
                  checked={selectedUsers?.length === filteredUsers?.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
              </div>
              <div className="col-span-3">User</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Joined</div>
              <div className="col-span-2">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredUsers?.map((user) => (
              <div
                key={user?.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 py-4 bg-muted/30 hover:bg-muted/50 rounded-lg transition-smooth border border-transparent hover:border-border"
              >
                <div className="lg:col-span-1 flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => handleSelectUser(user?.id)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                </div>

                <div className="lg:col-span-3 flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-sm text-foreground">{user?.full_name || 'N/A'}</p>
                    <p className="font-body text-xs text-muted-foreground">{user?.email || 'No email'}</p>
                  </div>
                </div>

                <div className="lg:col-span-2 flex items-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user?.role)}`}>
                    {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1)}
                  </span>
                </div>

                <div className="lg:col-span-2 flex items-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(user?.account_status || 'active')}`}>
                    {user?.account_status?.charAt(0)?.toUpperCase() + user?.account_status?.slice(1) || 'Active'}
                  </span>
                </div>

                <div className="lg:col-span-2 flex items-center">
                  <p className="font-body text-sm text-muted-foreground">
                    {new Date(user?.created_at)?.toLocaleDateString()}
                  </p>
                </div>

                <div className="lg:col-span-2 flex items-center space-x-2">
                  {user?.account_status === 'suspended' ? (
                    <button
                      onClick={() => handleActivateUser(user?.id)}
                      disabled={actionLoading}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-600 rounded-md transition-smooth text-xs font-medium"
                    >
                      <Icon name="CheckCircle" size={14} />
                      <span>Activate</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSuspendUser(user?.id)}
                      disabled={actionLoading}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-600 rounded-md transition-smooth text-xs font-medium"
                    >
                      <Icon name="XCircle" size={14} />
                      <span>Suspend</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="font-heading text-2xl font-bold text-foreground">{users?.length}</p>
            <p className="font-body text-xs text-muted-foreground">Total Users</p>
          </div>
          <div className="text-center">
            <p className="font-heading text-2xl font-bold text-green-600">
              {users?.filter((u) => u?.account_status === 'active' || !u?.account_status)?.length}
            </p>
            <p className="font-body text-xs text-muted-foreground">Active</p>
          </div>
          <div className="text-center">
            <p className="font-heading text-2xl font-bold text-red-600">
              {users?.filter((u) => u?.account_status === 'suspended')?.length}
            </p>
            <p className="font-body text-xs text-muted-foreground">Suspended</p>
          </div>
          <div className="text-center">
            <p className="font-heading text-2xl font-bold text-yellow-600">
              {users?.filter((u) => u?.verification_status === 'pending')?.length}
            </p>
            <p className="font-body text-xs text-muted-foreground">Pending Verification</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementCenter;